import { useCallback } from 'react'
import { ErrorCode } from '@ancon/wildcat-types'

import useAppStore from '../../../../../store/hooks/useAppStore'
import useAppDispatch from '../../../../../store/hooks/useAppDispatch'
import useAppSelector from '../../../../../store/hooks/useAppSelector'
import { currentCheckoutItemsSelector } from '../../../../checkout/store/checkoutSelectors'
import { deleteCheckoutItems } from '../../../../checkout/store/checkoutThunks'
import { isPreOrderCheckoutSelector } from '../../../../preOrder/store/preOrderSelectors'
import { deletePreOrderCheckoutItem } from '../../../../preOrder/store/preOrderThunks'
import { appErrorModalConfigSelector } from '../../../store/appSelectors'
import { InformativeError } from '../../../types'

/** Removes checkout items the backend reported as no longer available (e.g. a happy hour item after the window ended) */
export function useDeleteInvalidCheckoutItems() {
  const store = useAppStore()
  const dispatch = useAppDispatch()

  const errorModalConfig = useAppSelector(appErrorModalConfigSelector)
  const isPreOrderCheckout = useAppSelector(isPreOrderCheckoutSelector)

  return useCallback(async () => {
    const invalidDataErrorModalConfig =
      errorModalConfig as InformativeError<ErrorCode.RequestDataNotValid>

    const unavailableVariantIds = (
      invalidDataErrorModalConfig?.additionalData || []
    ).map(data => data.variantId)

    if (unavailableVariantIds.length === 0) return

    const currentCheckoutItems = currentCheckoutItemsSelector(store.getState())
    const checkoutItemIds = currentCheckoutItems.reduce<string[]>(
      (acc, item) => {
        if (unavailableVariantIds.includes(item.product.variantId)) {
          acc.push(item.id)
        }
        return acc
      },
      [],
    )

    if (checkoutItemIds.length === 0) return

    if (isPreOrderCheckout) {
      await dispatch(deletePreOrderCheckoutItem({ checkoutItemIds })).unwrap()
    } else {
      await dispatch(deleteCheckoutItems({ checkoutItemIds })).unwrap()
    }
  }, [dispatch, errorModalConfig, isPreOrderCheckout, store])
}

export default useDeleteInvalidCheckoutItems
